/**
 * AppICEMAP — Report actions
 * Shared behaviour for acting on a report from anywhere in the UI (list card,
 * map popup, detail sheet), so the confirmation copy and the store update stay
 * identical wherever the action is triggered from.
 */

import { deleteReport } from "../api/reports.js";
import { dispatch, state } from "../store/index.js";
import { confirmAction } from "../components/shared/ConfirmDialog.js";
import { show as showToast } from "../components/shared/Toast.js";
import { canDeleteReport } from "../utils/permissions.js";

/**
 * Can the signed-in user delete this report? Use it to decide whether to render
 * the action at all.
 *
 * @param {object} report
 * @returns {boolean}
 */
export function canDelete(report) {
	return canDeleteReport(state.user, report);
}

/**
 * Ask for confirmation and delete the report.
 * Removes it from the store on success, which makes the marker, the list card
 * and any open sheet disappear through the usual subscriptions.
 *
 * @param {object} report
 * @returns {Promise<boolean>} true when it was deleted
 */
export async function confirmAndDeleteReport(report) {
	if (!report?.id) return false;

	const ok = await confirmAction({
		title: "¿Borrar este reporte?",
		message:
			"Se eliminará para toda la comunidad, junto con sus confirmaciones y comentarios. Esta acción no se puede deshacer.",
		detail: report.title || "Sin título",
		confirmLabel: "Borrar",
		cancelLabel: "Cancelar",
		danger: true,
	});

	if (!ok) return false;

	try {
		await deleteReport(report.id);
		dispatch("REMOVE_REPORT", report.id);
		showToast("Reporte borrado", "success");
		return true;
	} catch (err) {
		const detail = err?.errors?.[0]?.message || err?.message || String(err);
		console.error("[Reports] Delete failed:", detail);
		showToast(
			detail.toLowerCase().includes("permission") ||
				detail.toLowerCase().includes("forbidden")
				? "No tienes permiso para borrar reportes."
				: "No se pudo borrar el reporte. Inténtalo de nuevo.",
			"error",
		);
		return false;
	}
}
